import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getLineProfile, unlinkLineAccount } from '../../../services/memberSettingsApi';
import { isTempEmail } from '../../../utils/email';
import './LineLinkingSection.css';

const LineLinkingSection = () => {
  const { user } = useAuth();
  const [lineProfile, setLineProfile] = useState<{
    isLinked: boolean;
    displayName?: string;
    pictureUrl?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchLineProfile();
  }, []);

  const fetchLineProfile = async () => {
    try {
      const result = await getLineProfile();
      if (result.success && result.data) {
        setLineProfile({
          isLinked: result.data.isLinked,
          displayName: result.data.displayName,
          pictureUrl: result.data.pictureUrl,
        });
      }
    } catch {
      console.error('Failed to fetch LINE profile');
    }
  };

  const handleLinkLine = () => {
    // Navigate to LINE login page
    const linkMode = 'true';
    const redirectUrl = encodeURIComponent(window.location.origin + '/member?tab=settings');
    window.location.href = `/line/login?link_mode=${linkMode}&redirect=${redirectUrl}`;
  };

  const handleUnlinkLine = async () => {
    if (!confirm('確定要解除綁定 LINE 帳號嗎？解除後將無法使用 LINE 登入及接收通知。')) {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await unlinkLineAccount();
      if (result.success) {
        setMessage({ type: 'success', text: 'LINE 帳號已解除綁定' });
        setLineProfile({ isLinked: false });
      } else {
        setMessage({ type: 'error', text: '解除綁定失敗，請稍後再試' });
      }
    } catch {
      setMessage({ type: 'error', text: '網路錯誤，請稍後再試' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  // Only show LINE linking section for LINE users (users with lineUserId)
  // Email-registered users should not see this section
  const isLineUser = !!user.lineUserId;

  if (!isLineUser) {
    return null;
  }

  return (
    <div className="line-linking-section">
      <h3 className="line-linking-section__title">LINE 帳號設定</h3>

      {message && (
        <div className={`line-linking-section__alert line-linking-section__alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {isLineUser ? (
        <div className="line-linking-section__linked">
          <div className="line-linking-section__info">
            <div className="line-linking-section__avatar">
              {lineProfile?.pictureUrl ? (
                <img src={lineProfile.pictureUrl} alt={lineProfile.displayName} />
              ) : (
                <div className="line-linking-section__avatar-placeholder">
                  💬
                </div>
              )}
            </div>
            <div className="line-linking-section__details">
              <div className="line-linking-section__name">
                {lineProfile?.displayName || 'LINE 用戶'}
              </div>
              <div className="line-linking-section__status">
                ✓ 已綁定
              </div>
            </div>
          </div>
          <button
            onClick={handleUnlinkLine}
            className="line-linking-section__unlink"
            disabled={isLoading || isTempEmail(user?.email || '')}
            title={isTempEmail(user?.email || '') ? '請先綁定電子郵件後才能解除 LINE 綁定' : undefined}
          >
            {isLoading ? '處理中...' : '解除綁定'}
          </button>
          {isTempEmail(user?.email || '') && (
            <small className="line-linking-section__hint">
              ⚠️ 請先綁定電子郵件後才能解除 LINE 綁定
            </small>
          )}
        </div>
      ) : (
        <div className="line-linking-section__unlinked">
          <p className="line-linking-section__description">
            綁定 LINE 帳號後，您可以使用 LINE 登入，並接收重要通知推送。
          </p>
          <button
            onClick={handleLinkLine}
            className="line-linking-section__link"
          >
            <span className="line-linking-section__icon">💬</span>
            綁定 LINE 帳號
          </button>
        </div>
      )}
    </div>
  );
};

export default LineLinkingSection;
